import type {
  AttackDefinition,
  DamageType,
  TowerDefinition,
  TowerInstance,
  TowerUpgradeDefinition,
} from "../domain/types";

const attack = (
  kind: AttackDefinition["kind"],
  damageType: DamageType,
  damage: number,
  intervalMs: number,
  projectileSpeed = 8,
  splashRadius = 0,
): AttackDefinition => ({
  kind,
  damageType,
  damage,
  intervalMs,
  projectileSpeed,
  splashRadius,
  splashFalloff: 0.62,
  chainTargets: 0,
  chainLoss: 0.75,
  armorBreak: 0,
  armorBreakDurationMs: 0,
  simultaneousTargets: 1,
});
const upgrade = (
  level: number,
  name: string,
  description: string,
  cost: number,
  values: Omit<TowerUpgradeDefinition, "level" | "name" | "description" | "cost"> = {},
): TowerUpgradeDefinition => ({ level, name, description, cost, ...values });

interface Spec {
  id: string;
  name: string;
  description: string;
  icon: string;
  cost: number;
  range: number;
  attack: AttackDefinition;
  tags: string[];
  unlock: string;
  branchA: string;
  branchB: string;
}
const makeTower = (s: Spec): TowerDefinition => ({
  id: s.id,
  displayName: s.name,
  description: s.description,
  icon: s.icon,
  texture: `tower-${s.id}`,
  footprint: [{ x: 0, y: 0 }],
  cost: s.cost,
  sellRefundRate: 0.7,
  range: s.range,
  attack: s.attack,
  validTargetClasses: s.tags.includes("anti-air") ? ["ground", "flying"] : ["ground"],
  unlockRequirement: s.unlock,
  audio: { fire: `${s.id}-fire`, impact: `${s.id}-impact` },
  effects: { projectile: s.id, impact: `${s.id}-impact` },
  tags: s.tags,
  balanceNotes: `${s.name}: ${s.branchA} or ${s.branchB}.`,
  upgrades: [
    upgrade(
      2,
      "Royal Training",
      `Damage ${s.attack.damage} → ${Math.round(s.attack.damage * 1.3)}`,
      Math.round(s.cost * 0.65),
      { attack: { damage: Math.round(s.attack.damage * 1.3) }, branch: "shared" },
    ),
    upgrade(
      3,
      "Reinforced Works",
      `Range ${s.range} → ${(s.range + 0.55).toFixed(2)}; interval ${(s.attack.intervalMs / 1000).toFixed(2)}s → ${((s.attack.intervalMs * 0.86) / 1000).toFixed(2)}s`,
      Math.round(s.cost * 0.9),
      {
        range: s.range + 0.55,
        attack: { intervalMs: Math.round(s.attack.intervalMs * 0.86) },
        branch: "shared",
      },
    ),
    upgrade(
      4,
      s.branchA,
      `${s.branchA}: stronger attacks and greater reach`,
      Math.round(s.cost * 1.35),
      {
        branch: "a",
        branchName: s.branchA,
        capability: `${s.id}-branch-a`,
        range: s.range + 0.9,
        attack: { damage: Math.round(s.attack.damage * 1.72) },
      },
    ),
    upgrade(
      4,
      s.branchB,
      `${s.branchB}: faster attacks and broader crowd control`,
      Math.round(s.cost * 1.35),
      {
        branch: "b",
        branchName: s.branchB,
        capability: `${s.id}-branch-b`,
        attack: {
          intervalMs: Math.max(120, Math.round(s.attack.intervalMs * 0.62)),
          splashRadius: s.attack.splashRadius > 0 ? s.attack.splashRadius + 0.5 : 0,
          simultaneousTargets: Math.max(1, s.attack.simultaneousTargets + 1),
        },
      },
    ),
    upgrade(
      5,
      `${s.branchA} Mastery`,
      `Master ${s.branchA} with exceptional damage and range`,
      Math.round(s.cost * 1.8),
      {
        branch: "a",
        branchName: s.branchA,
        capability: `${s.id}-branch-a-mastery`,
        range: s.range + 1.25,
        attack: {
          damage: Math.round(s.attack.damage * 2.25),
          armorBreak: Math.max(s.attack.armorBreak, 0.12),
          armorBreakDurationMs: Math.max(s.attack.armorBreakDurationMs, 3200),
        },
      },
    ),
    upgrade(
      5,
      `${s.branchB} Mastery`,
      `Master ${s.branchB} with relentless speed and target coverage`,
      Math.round(s.cost * 1.8),
      {
        branch: "b",
        branchName: s.branchB,
        capability: `${s.id}-branch-b-mastery`,
        attack: {
          intervalMs: Math.max(100, Math.round(s.attack.intervalMs * 0.48)),
          splashRadius: s.attack.splashRadius > 0 ? s.attack.splashRadius + 1 : 0,
          simultaneousTargets: Math.max(2, s.attack.simultaneousTargets + 2),
          chainTargets: s.attack.chainTargets > 0 ? s.attack.chainTargets + 2 : 0,
        },
      },
    ),
  ],
});

const specs: Spec[] = [
  {
    id: "archer",
    name: "Archer Tower",
    description: "Fast, economical single-target defense.",
    icon: "A",
    cost: 70,
    range: 3.6,
    attack: attack("projectile", "physical", 20, 820, 10),
    tags: ["starter", "direct"],
    unlock: "default",
    branchA: "Marksman",
    branchB: "Volley",
  },
  {
    id: "rocket",
    name: "Rocket Bastion",
    description: "Alchemical rockets punish clustered runners.",
    icon: "R",
    cost: 145,
    range: 4.2,
    attack: attack("splash", "explosive", 42, 1650, 7, 1.25),
    tags: ["splash"],
    unlock: "default",
    branchA: "Siege Warhead",
    branchB: "Cluster Payload",
  },
  {
    id: "lightning",
    name: "Lightning Spire",
    description: "Heavy magical strike that can chain between runners.",
    icon: "L",
    cost: 190,
    range: 4,
    attack: { ...attack("instant-chain", "electrical", 96, 2150, 0, 1.7), chainTargets: 1 },
    tags: ["electrical", "chain"],
    unlock: "default",
    branchA: "Overload",
    branchB: "Crown Storm",
  },
  {
    id: "cannon",
    name: "King's Cannon",
    description: "Slow royal gun with devastating splash damage.",
    icon: "C",
    cost: 260,
    range: 4.75,
    attack: {
      ...attack("splash", "explosive", 150, 3050, 5.5, 1.7),
      armorBreak: 0.12,
      armorBreakDurationMs: 3000,
    },
    tags: ["splash", "heavy"],
    unlock: "default",
    branchA: "Siegebreaker",
    branchB: "Shattershot",
  },
  {
    id: "crossbow",
    name: "Crossbow Tower",
    description: "Armor-piercing bolts for disciplined targets.",
    icon: "X",
    cost: 105,
    range: 4.1,
    attack: attack("piercing", "physical", 46, 1320, 12),
    tags: ["armor-piercing"],
    unlock: "castle-approach:easy",
    branchA: "Bodkin Line",
    branchB: "Repeater",
  },
  {
    id: "ballista",
    name: "Grand Ballista",
    description: "Extreme-range anti-elite bolt thrower.",
    icon: "B",
    cost: 225,
    range: 6.2,
    attack: attack("piercing", "physical", 175, 3300, 15),
    tags: ["long-range", "elite"],
    unlock: "royal-courtyard:easy",
    branchA: "Dragon Bolt",
    branchB: "Skewer Line",
  },
  {
    id: "frost",
    name: "Frost Tower",
    description: "Chilling pulses slow dense formations.",
    icon: "F",
    cost: 125,
    range: 3.2,
    attack: attack("aura", "frost", 12, 900, 0, 1.8),
    tags: ["slow", "control"],
    unlock: "siege-grounds:easy",
    branchA: "Deep Freeze",
    branchB: "Winter Field",
  },
  {
    id: "flame",
    name: "Flame Tower",
    description: "Short-range fire cone burns packed runners.",
    icon: "🔥",
    cost: 135,
    range: 2.6,
    attack: attack("cone", "fire", 28, 520, 0, 1.1),
    tags: ["burn", "cone"],
    unlock: "siege-grounds:medium",
    branchA: "Inferno",
    branchB: "Cinderstorm",
  },
  {
    id: "poison",
    name: "Poison Tower",
    description: "Persistent venom excels against high health.",
    icon: "P",
    cost: 150,
    range: 4,
    attack: attack("projectile", "poison", 18, 1100, 8),
    tags: ["dot", "poison"],
    unlock: "enchanted-forest:easy",
    branchA: "Virulent",
    branchB: "Contagion",
  },
  {
    id: "earthshaker",
    name: "Earthshaker",
    description: "Slow ground pulses stagger crowds.",
    icon: "E",
    cost: 210,
    range: 3,
    attack: attack("aura", "physical", 88, 2600, 0, 2),
    tags: ["stun", "splash"],
    unlock: "enchanted-forest:medium",
    branchA: "Fault Line",
    branchB: "Aftershock",
  },
  {
    id: "time",
    name: "Time Tower",
    description: "A support field that slows nearby runners.",
    icon: "T",
    cost: 180,
    range: 3.4,
    attack: attack("aura", "arcane", 0, 1000, 0, 2.2),
    tags: ["support", "slow"],
    unlock: "frozen-keep:easy",
    branchA: "Stasis",
    branchB: "Wide Horizon",
  },
  {
    id: "alchemist",
    name: "Alchemist Tower",
    description: "Splashing flasks reduce armor and resistance.",
    icon: "⚗",
    cost: 165,
    range: 4.1,
    attack: {
      ...attack("splash", "poison", 20, 1450, 7, 1.2),
      armorBreak: 0.14,
      armorBreakDurationMs: 3500,
    },
    tags: ["debuff", "splash"],
    unlock: "frozen-keep:medium",
    branchA: "Corrosive",
    branchB: "Catalyst",
  },
  {
    id: "banner",
    name: "Banner Tower",
    description: "Strengthens nearby defensive towers.",
    icon: "⚑",
    cost: 175,
    range: 3.2,
    attack: attack("aura", "physical", 0, 1000, 0, 3.2),
    tags: ["support", "buff"],
    unlock: "coastal-citadel:easy",
    branchA: "War Standard",
    branchB: "Royal Reach",
  },
  {
    id: "scout-tower",
    name: "Scout Tower",
    description: "Reveals stealth and extends awareness.",
    icon: "S",
    cost: 110,
    range: 4.8,
    attack: attack("projectile", "physical", 10, 1200, 12),
    tags: ["reveal", "support", "anti-air"],
    unlock: "enchanted-forest:hard",
    branchA: "Far Sight",
    branchB: "Spotter Network",
  },
  {
    id: "sniper",
    name: "Sniper Tower",
    description: "Extreme-range critical finisher.",
    icon: "N",
    cost: 240,
    range: 7,
    attack: attack("projectile", "physical", 210, 3600, 18),
    tags: ["critical", "long-range"],
    unlock: "coastal-citadel:medium",
    branchA: "Deadeye",
    branchB: "Royal Marksman",
  },
  {
    id: "execution",
    name: "Execution Tower",
    description: "Deals immense damage to weakened runners.",
    icon: "K",
    cost: 205,
    range: 3.8,
    attack: attack("projectile", "physical", 62, 1500, 10),
    tags: ["execute", "finisher"],
    unlock: "underground-stronghold:easy",
    branchA: "Mercyless",
    branchB: "Headsman",
  },
  {
    id: "bombard",
    name: "Bombard",
    description: "Long-range delayed artillery for broad zones.",
    icon: "D",
    cost: 285,
    range: 6,
    attack: attack("artillery", "explosive", 185, 3400, 4, 2.3),
    tags: ["artillery", "splash"],
    unlock: "underground-stronghold:medium",
    branchA: "Grand Mortar",
    branchB: "Rolling Barrage",
  },
  {
    id: "arcane",
    name: "Arcane Tower",
    description: "Magic bolts bypass much conventional armor.",
    icon: "M",
    cost: 195,
    range: 4.4,
    attack: attack("projectile", "arcane", 82, 1280, 11),
    tags: ["magic", "armor-bypass"],
    unlock: "volcanic-fortress:easy",
    branchA: "Rift Lance",
    branchB: "Mana Cascade",
  },
  {
    id: "gold",
    name: "Gold Tower",
    description: "Controlled economy support with capped returns.",
    icon: "G",
    cost: 250,
    range: 2.8,
    attack: attack("income", "physical", 0, 8000, 0),
    tags: ["economy", "support"],
    unlock: "12-stars",
    branchA: "Royal Mint",
    branchB: "Bounty Office",
  },
  {
    id: "dragon",
    name: "Dragon Roost",
    description: "Endgame sweeping fire with a long cooldown.",
    icon: "🐉",
    cost: 475,
    range: 5.2,
    attack: attack("cone", "fire", 260, 4200, 0, 2.4),
    tags: ["endgame", "splash", "anti-air"],
    unlock: "volcanic-fortress:medium",
    branchA: "Ancient Flame",
    branchB: "Sky Tyrant",
  },
];

export const TOWERS: Readonly<Record<string, TowerDefinition>> = Object.freeze(
  Object.fromEntries(specs.map((s) => [s.id, makeTower(s)])),
);

export function towerAtLevel(
  definition: TowerDefinition,
  level: number,
  branch?: "a" | "b",
): { range: number; attack: AttackDefinition; tags: readonly string[] } {
  let range = definition.range;
  let a = { ...definition.attack };
  const tags = [...definition.tags];
  for (const change of definition.upgrades.filter(
    (u) =>
      u.level <= level && (!u.branch || u.branch === "shared" || !branch || u.branch === branch),
  )) {
    if (change.range !== undefined) range = change.range;
    if (change.attack) a = { ...a, ...change.attack };
    if (change.tags) tags.push(...change.tags);
    if (change.capability) tags.push(change.capability);
  }
  return { range, attack: a, tags };
}

export function towerStatsForInstance(
  definition: TowerDefinition,
  tower: TowerInstance,
): { range: number; attack: AttackDefinition; tags: readonly string[] } {
  const base = towerAtLevel(definition, tower.level, tower.branch);
  return {
    range: base.range + (tower.rangeBonus ?? 0),
    tags: base.tags,
    attack: {
      ...base.attack,
      damage: base.attack.damage * (tower.damageMultiplier ?? 1),
      intervalMs: Math.max(100, base.attack.intervalMs * (tower.intervalMultiplier ?? 1)),
      splashRadius: base.attack.splashRadius + (tower.splashBonus ?? 0),
      simultaneousTargets: Math.max(1, base.attack.simultaneousTargets + (tower.targetBonus ?? 0)),
      armorBreak: Math.min(0.9, base.attack.armorBreak + (tower.armorBreakBonus ?? 0)),
    },
  };
}
